'use client';

import { useState, useMemo } from 'react';
import { Popover, PopoverButton, PopoverPanel, Portal } from '@headlessui/react';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { useOrganizationFormatters } from '@/lib/platform/hooks/use-organization-locale';

interface DatePickerProps {
  value: string; // YYYY-MM-DD format
  onChange: (date: string) => void;
  label?: string;
  required?: boolean;
  id?: string;
}

export default function DatePicker({ value, onChange, label, required, id }: DatePickerProps) {
  const { darkMode } = useDarkMode();
  const { formatDate } = useOrganizationFormatters();

  // Parse the current value
  const currentDate = value ? new Date(value + 'T00:00:00') : new Date();
  const [viewMonth, setViewMonth] = useState(currentDate.getMonth());
  const [viewYear, setViewYear] = useState(currentDate.getFullYear());

  // Format date for display using organization settings
  const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return '';
    // Convert YYYY-MM-DD to Date object and format using org settings
    const date = new Date(dateStr + 'T00:00:00');
    return formatDate(date);
  };

  // Generate calendar days
  const days = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay()); // Start from Sunday

    const daysArray = [];
    const currentDay = new Date(startDate);

    // Helper function to format date as YYYY-MM-DD in local timezone
    const formatLocalDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    for (let i = 0; i < 42; i++) {
      // 6 weeks
      const dateStr = formatLocalDate(currentDay);
      const isCurrentMonth = currentDay.getMonth() === viewMonth;
      const todayStr = formatLocalDate(new Date());
      const isToday = dateStr === todayStr;
      const isSelected = dateStr === value;

      daysArray.push({
        date: dateStr,
        day: currentDay.getDate(),
        isCurrentMonth,
        isToday,
        isSelected,
      });

      currentDay.setDate(currentDay.getDate() + 1);
    }

    return daysArray;
  }, [viewMonth, viewYear, value]);

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const handleDateSelect = (dateStr: string, close: () => void) => {
    onChange(dateStr);
    close();
  };

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  return (
    <div>
      {label && (
        <label
          htmlFor={id}
          className={`mb-2 block text-sm/6 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
        >
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <Popover className="relative">
        {({ close }) => (
          <>
            <PopoverButton
              id={id}
              className={`block flex w-full items-center justify-between rounded-md px-3 py-1.5 text-base outline-1 -outline-offset-1 transition-all sm:text-sm/6 ${
                darkMode
                  ? 'bg-white/5 text-gray-100 outline-white/10'
                  : 'bg-white text-gray-900 outline-gray-300'
              } ${
                darkMode
                  ? 'focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-500'
                  : 'focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600'
              } `}
            >
              <span>{value ? formatDateDisplay(value) : 'Select date'}</span>
              <CalendarIcon className="size-5" />
            </PopoverButton>

            <Portal>
              <PopoverPanel
                anchor="bottom start"
                className={`z-[100] mt-2 min-w-[280px] rounded-lg p-4 shadow-xl ring-1 ${
                  darkMode
                    ? 'bg-gray-800 shadow-black/50 ring-white/10'
                    : 'bg-white shadow-black/10 ring-black/5'
                }`}
              >
                {/* Calendar Header */}
                <div className="mb-4 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={handlePrevMonth}
                    className={`rounded p-1 hover:bg-gray-500/10 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                  >
                    <ChevronLeftIcon className="size-5" />
                  </button>
                  <div
                    className={`text-sm font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
                  >
                    {monthNames[viewMonth]} {viewYear}
                  </div>
                  <button
                    type="button"
                    onClick={handleNextMonth}
                    className={`rounded p-1 hover:bg-gray-500/10 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                  >
                    <ChevronRightIcon className="size-5" />
                  </button>
                </div>

                {/* Day Names */}
                <div className="mb-2 grid grid-cols-7 gap-1">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                    <div
                      key={day}
                      className={`py-2 text-center text-xs font-medium ${
                        darkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-1">
                  {days.map((day, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleDateSelect(day.date, close)}
                      className={`rounded py-2 text-sm transition-colors ${!day.isCurrentMonth && (darkMode ? 'text-gray-600' : 'text-gray-400')} ${day.isCurrentMonth && !day.isSelected && (darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-900 hover:bg-gray-100')} ${day.isSelected && 'bg-indigo-600 font-semibold text-white'} ${day.isToday && !day.isSelected && 'font-semibold ring-1 ring-indigo-600'} `}
                    >
                      {day.day}
                    </button>
                  ))}
                </div>
              </PopoverPanel>
            </Portal>
          </>
        )}
      </Popover>
    </div>
  );
}
